import { DrawerHeader } from "../../../../../components/drawer/drawer-header";
import { memo, useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useProjectNavigate } from "../../../../../common/utils/useProjectNavigate";
import moment from "moment";
import { Drawer } from "../../../../../components/drawer/drawer";
import InfinityTable from "../../../../../components/infinity-table/infinity-table";
import ApiAnalytics from "../../../../../api/analytics";
import InfoCardWrapper from "../../../../../components/info-card/info-card-wrapper";
import StatisticChart from "../../../../../components/statistic-chart/statistic-chart";
import DropdownFilters from "../../../../../components/dropdown-filters/dropdown-filters";
import { Button, Tabs } from "antd";
import PageContentLoader from "../../../../../components/loader/page-content-loader";
import debounce from "../../../../../common/utils/debounce";

const AnalyticsCardInside = memo(
  ({
    analytics,
    close,
    subjects,
    cohorts,
    initialFilters,
  }) => {
    const [filters, setFilters] = useState({
      cohortIds: initialFilters?.cohortIds || null,
      period: initialFilters?.period || null,
      subjectIds: null,
    });
    const filterItems = [
      { name: "Date peroid", id: "period", period: true },
      { name: "Subject ID", id: "subjectIds", children: subjects },
      { name: "Cohorts", id: "cohortIds", children: cohorts },
    ];
    const [loading, setLoading] = useState(true);
    const [localAnalytics, setLocalAnalytics] = useState(analytics); 
    const { t } = useTranslation();
    const navigate = useProjectNavigate();

    const debouncedFetch = useCallback(
      debounce((currentFilters, currentAnalytics, onSuccess) => {
        if (Array.isArray(currentFilters.period)) {
          currentFilters = {
            ...currentFilters,
            createdAtEnd: currentFilters.period?.[currentFilters.period.length - 1]?.end,
            createdAtStart: currentFilters.period?.[currentFilters.period.length - 1]?.start,
          };
        }
        if (!currentFilters.period || (currentFilters.period && currentFilters.createdAtStart)) {
          setLoading(true);
          ApiAnalytics.getTaskAnalyticsById(currentAnalytics.id, currentFilters)
          .then((response) => {
            if (onSuccess) {
              onSuccess(response); 
            }
          })
          .catch(error => {
            console.error("API request failed:", error);
            setLoading(false);
          });
        }
      }, 1000),
      []
    );

    const transformAnalyticsToData = (analytics) => {
      return [
        {
          title: analytics.adherence || 0,
          subtitle: t("analytics.card.adherence_rate"),
          isPercent: true,
        },
        {
          title: analytics.statusCounts?.COMPLETED || 0,
          subtitle: t("analytics.card.complete"),
          isPercent: false,
        },
        {
          title: analytics.statusCounts?.PENDING || 0,
          subtitle: t("analytics.card.pending"),
          isPercent: false,
        },
        {
          title: analytics.statusCounts?.DISMISSED || 0,
          subtitle: t("analytics.card.dismissed"),
          isPercent: false,
        },
        {
          title: analytics.statusCounts?.INCOMPLETED || 0,
          subtitle: t("analytics.card.incomplete"),
          isPercent: false,
        }
      ];
    };

    const data = transformAnalyticsToData(localAnalytics);

    const updateFilterValue = (filter) => {
      setFilters(filter);
    };

    const [didApplyInitial, setDidApplyInitial] = useState(false);

    const toSubjectTask = (record) => {
      if (!record?.subjectId || !record?.id) return;

      navigate(
        `/subject-management/subject/${record.subjectId}?subjectTaskId=${record.id}#tasks`
      );
    };

    // Tasks expose a data report (survey questions / completion survey); open it
    // carrying the current filters so the report opens on the same slice the
    // user was looking at.
    const openSurveyReport = () => {
      const params = new URLSearchParams();
      params.set("dailyTaskId", localAnalytics.id);

      const period =
        Array.isArray(filters.period) && filters.period.length
          ? filters.period[filters.period.length - 1]
          : null;
      const toStartIso = (v) => {
        if (!v) return null;
        const d = new Date(v);
        d.setHours(0, 0, 0, 0);
        return d.toISOString();
      };
      const toEndIso = (v) => {
        if (!v) return null;
        const d = new Date(v);
        d.setHours(23, 59, 59, 999);
        return d.toISOString();
      };

      if (period?.start) params.set("startDate", toStartIso(period.start));
      if (period?.end) params.set("endDate", toEndIso(period.end));
      if (filters.cohortIds?.length) params.set("cohortIds", filters.cohortIds.join(","));
      if (filters.subjectIds?.length) params.set("subjectIds", filters.subjectIds.join(","));

      navigate(`/analytics/reports/surveys?${params.toString()}`);
    };

    useEffect(() => {
      if (initialFilters && !didApplyInitial) {
        const updatedFilters = {
          ...filters,
          ...initialFilters,
          createdAtStart: initialFilters?.period?.[initialFilters.period.length-1]?.start,
          createdAtEnd: initialFilters?.period?.[initialFilters.period.length-1]?.end,
        };
        setFilters(updatedFilters);
        setDidApplyInitial(true);
        return; 
      }

      let isActive = true;
      // debouncedFetch(filters, analytics);
      const executeFetch = (currentFilters) => {
        debouncedFetch(currentFilters, analytics, (response) => {
          if (isActive) { 
            const updatedAnalytics = {
              ...analytics,
              ...response.data.task,
            };
            setLocalAnalytics(updatedAnalytics);
            setLoading(false);
          }
        });
      };

      executeFetch(filters);

      return () => {
        isActive = false;
      };

    }, [filters, initialFilters, didApplyInitial, analytics, debouncedFetch]);

    const columnsComplete = [
      {
        title: t("analytics.table.subjectId"),
        dataIndex: "subjectId",
        key: "subjectId",
        render: (_, analytics) => <span>{analytics.Subject.displayId}</span>,
        width: "30%",
      },
      {
        title: t("analytics.table.cohort"),
        dataIndex: "cohort",
        key: "cohort",
        render: (_, analytics) => <span>{analytics.cohort}</span>,
        width: "30%",
      },
      {
        title: t("analytics.table.completion_date"),
        dataIndex: "completion_date",
        key: "completion_date",
        render: (_, analytics) =>
          analytics.completedAt
            ? `${moment(analytics.completedAt).utc().format("DD MMM YYYY, HH:mm")}, UTC`
            : "N/A",
        width: "40%",
      },
    ];

    const columnsOther = [
      {
        title: t("analytics.table.subjectId"),
        dataIndex: "subjectId",
        key: "subjectId",
        render: (_, analytics) => <span>{analytics.Subject.displayId}</span>,
        width: "30%",
      },
      {
        title: t("analytics.table.cohort"),
        dataIndex: "cohort",
        key: "cohort",
        render: (_, analytics) => <span>{analytics.cohort}</span>,
        width: "30%",
      },
      {
        title: t("analytics.table.date"),
        dataIndex: "planned_date",
        key: "planned_date",
        render: (_, analytics) =>
          analytics.plannedAt
            ? `${moment(analytics.plannedAt).utc().format("DD MMM YYYY, HH:mm")}, UTC`
            : "N/A",
        width: "40%",
      },
    ];

    const columnsDismissed = [
      {
        title: t("analytics.table.subjectId"),
        dataIndex: "subjectId",
        key: "subjectId",
        render: (_, analytics) => <span>{analytics.Subject.displayId}</span>,
        width: "20%",
      },
      {
        title: t("analytics.table.cohort"),
        dataIndex: "cohort",
        key: "cohort",
        render: (_, analytics) => <span>{analytics.cohort}</span>,
        width: "20%",
      },
      {
        title: t("analytics.table.dismiss_reason"),
        dataIndex: "reason",
        key: "reason",
        render: (_, analytics) => {
          const reasonType = analytics.dismissReasonType?.toLowerCase(); // Приводимо до нижнього регістру
          if (reasonType === "other") {
            return <span>Other: {analytics.dismissReasonText}</span>;
          }
          return <span>{analytics.dismissReasonType}</span>;
        },
        width: "40%",
      },
      {
        title: t("analytics.table.dismiss_date"),
        dataIndex: "dismissedAt",
        key: "dismissedAt",
        render: (_, analytics) =>
          analytics.dismissedAt
            ? `${moment(analytics.dismissedAt).utc().format("DD MMM YYYY, HH:mm")}, UTC`
            : "N/A",
        width: "20%",
      },
    ];
    return (
      <>
        <DrawerHeader
          badge={localAnalytics?.Category?.name}
          title={localAnalytics.title}
          subtitle={localAnalytics.plans}
          onClose={close}
          extraRight={
            localAnalytics?.taskType === "survey" && (
              <Button onClick={openSurveyReport}>View Report</Button>
            )
          }
        />
        <div style={{ paddingBottom: 16 }} />
        <DropdownFilters
          filtersBy={filterItems}
          updateFilterValue={updateFilterValue}
          filter={initialFilters}
          solidBorder={false}
        />
        <div style={{ paddingBottom: 20 }} />
        {loading ? (
          <div style={{ position: 'relative' }}>
            <PageContentLoader />
          </div>
        ) : (
        <>
          <InfoCardWrapper data={data} />
          <div style={{ paddingBottom: 12 }} />
          <div className="analytics-tabs">
            <Tabs
              defaultActiveKey="1"
              // antd 6 takes its tabs as a list. Each pane's contents are
              // wrapped in a fragment: two of them hold sibling elements, which
              // an expression can only carry as one node.
              items={[
                {
                  key: "1",
                  label: t("analytics.card.complete"),
                  children: (
                    <>
                      <div style={{ paddingBottom: 12 }} />
                      {localAnalytics.questionAnswerStats &&
                        Object.keys(localAnalytics.questionAnswerStats).length > 0 && (
                          <>
                            {Object.entries(localAnalytics.questionAnswerStats).map(
                              ([questionText, answersArray]) => {
                                return (
                                  <div key={questionText} style={{ marginBottom: '16px' }}>
                                    <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                                      {questionText}
                                    </div>
                                    <StatisticChart data={answersArray} />
                                  </div>
                                );
                              }
                            )}
                          </>
                        )
                      }
                      <InfinityTable
                        data={localAnalytics?.statusGroups?.COMPLETED || []}
                        tableColumns={columnsComplete}
                        onHandleRowClick={toSubjectTask}
                      />
                    </>
                  ),
                },
                {
                  key: "2",
                  label: t("analytics.card.dismissed"),
                  children: (
                    <>
                      {localAnalytics.dismissReasonStats && localAnalytics.dismissReasonStats.length > 0 && (
                        <>
                          <StatisticChart
                            data={localAnalytics.dismissReasonStats}
                          />
                          <div style={{ paddingBottom: 12 }} />
                        </>
                      )}
                      <InfinityTable
                        data={localAnalytics?.statusGroups?.DISMISSED || []}
                        tableColumns={columnsDismissed}
                        onHandleRowClick={toSubjectTask}
                      />
                    </>
                  ),
                },
                {
                  key: "3",
                  label: t("analytics.card.pending"),
                  children: (
                    <InfinityTable
                      data={localAnalytics?.statusGroups?.PENDING || []}
                      tableColumns={columnsOther}
                      onHandleRowClick={toSubjectTask}
                    />
                  ),
                },
                {
                  key: "4",
                  label: t("analytics.card.incomplete"),
                  children: (
                    <InfinityTable
                      data={localAnalytics?.statusGroups?.INCOMPLETED || []}
                      tableColumns={columnsOther}
                      onHandleRowClick={toSubjectTask}
                    />
                  ),
                },
              ]}
            />
          </div>
        </>
        )}
      </>
    );
  }
);

const AnalyticsCard = ({
  analytics,
  isOpen,
  close,
  subjects,
  cohorts,
  initialFilters
}) => {
  return (
    <Drawer open={isOpen} size={900} closable={false} onClose={close}>
      <div style={{ paddingBottom: 48 }}>
        {isOpen && (
          <AnalyticsCardInside
            analytics={analytics}
            close={close}
            subjects={subjects}
            cohorts={cohorts}
            initialFilters={initialFilters}
          />
        )}
      </div>
    </Drawer>
  );
};

export default AnalyticsCard;
